/**
 * SearchService
 *
 * @author Copyright (c) 2021 Salesforce.com.
 * @ownership Appexchange Intelligence
 */
import { RECOMMENDATION_SERVICE_URL, SEARCH_SUGGESTIONS_SERVICE_URL, SPONSERED_RESULTS_LIMIT } from '~/modules/config';
import { FeatureFlags } from '~/modules/feature_flags';
import { isValidExternalAgentSearchSeed } from '~/modules/helpers/externalAgentSearchSeedHelper';
import { isConsultantAchievementFilterEnabled } from '~/modules/helpers/consultantAchievementFilterHelper';
import { getPreferredLanguage } from '~/modules/helpers/languageHelper';
import { handleException } from '~/modules/utilities/error_utilities';
import { HttpRequestConfig, gatewayParamsSerializer, http } from '~/modules/utilities/http';
import { ENGLISH_LANGUAGE_CODE } from '~/modules/utilities/language_utils';
import { wrapKeyword } from '~/modules/utilities/text';
import { SearchResult } from '../../schemas/search-schema-v1';
import { buildSearchResults } from '../helpers/searchServiceHelper';
import { FilterParams, SearchType } from '../stores/searchStore';

export interface Suggestion {
    suggestedKeyword: string;
    highlighted: string;
}

export interface SuggestionsResult {
    suggestions: Suggestion[];
}

export interface SearchRequestOptions {
    includeSearchQueryId?: boolean;
    seed?: number;
    /** Let a caller with feature context own error logging for this request. */
    suppressErrorLogging?: boolean;
}

export interface KeywordlessExternalAgentSearchRequest {
    page: number;
    pageSize: number;
    filterParams?: FilterParams;
    seed: number;
    sort?: string;
    rankingInformation?: boolean;
    searchQueryId?: string;
    prevSearchQueryId?: string;
    signal?: AbortSignal;
    suppressErrorLogging?: boolean;
}

type SearchApiQueryParams = {
    keyword?: string;
    type: string;
    page: string;
    pageSize: string;
    showSponsored?: string;
    sponsoredCount?: string;
    sort?: string;
    debugRankings?: string;
    language?: string;
    searchQueryId?: string;
    prevSearchQueryId?: string;
    seed?: string;
} & { [K in keyof FilterParams]: string };

const AGENTS_TECH_FILTER = 'force/copilot';

const ensureSlackTech = (tech: string[]): string[] => {
    return tech.includes('slack') ? tech : [...tech, 'slack'];
};

const getApiEndpoint = (): string => {
    return RECOMMENDATION_SERVICE_URL;
};

const getSuggestionsApiEndpoint = (): string => {
    return SEARCH_SUGGESTIONS_SERVICE_URL;
};

const getSearchEndpoint = (): string => {
    return `${getApiEndpoint()}/v3/listings`;
};

const mapSearchTypeToApiType = (type: string): string => {
    if (type === SearchType.AGENTS) {
        return SearchType.APPS;
    }
    // Map Slack to apps and inject tech=slack. Note: Slack-specific filterParams
    // (e.g., category=ai-apps-assistants, price=free) are forwarded verbatim to the apps API.
    // The backend currently ignores unrecognized Slack filters; W-23162803 tracks expanding
    // support for these params.
    if (type === SearchType.SLACK) {
        return SearchType.APPS;
    }
    return type;
};

const getSearchTermSuggestionsEndpoint = (): string => {
    return `${getSuggestionsApiEndpoint()}/v1/suggestions`;
};

const getSearchResults = async (
    keyword: string | undefined,
    type: string,
    page: number,
    pageSize: number,
    filterParams: FilterParams = {},
    sort: string | undefined = undefined,
    rankingInformation = false,
    searchQueryId?: string,
    prevSearchQueryId?: string,
    signal?: AbortSignal,
    requestOptions: SearchRequestOptions = {},
): Promise<SearchResult> => {
    const params: SearchApiQueryParams = {
        type: mapSearchTypeToApiType(type ? type : SearchType.APPS),
        page: page.toString(),
        pageSize: pageSize.toString(),
        language: getPreferredLanguage(),
    };

    if (SPONSERED_RESULTS_LIMIT) {
        params.sponsoredCount = String(SPONSERED_RESULTS_LIMIT);
    }

    if (keyword && keyword.length > 0) {
        params.keyword = keyword;
    }

    if (sort) {
        params.sort = sort;
    }

    if (rankingInformation) {
        params.debugRankings = String(rankingInformation);
    }

    if (!keyword && type === SearchType.EXTERNAL_AGENTS && requestOptions.seed !== undefined) {
        params.seed = String(requestOptions.seed);
    }

    const isDataCloudSearch = FeatureFlags.FF_DATA_CLOUD_SEARCH_UI;
    // External Agents are Data Cloud-native even while the broader storefront search flag is off.
    // Their keywordless pagination must keep the original query id or Data Cloud starts a new query
    // and returns the first page again.
    const shouldIncludeSearchQueryId =
        isDataCloudSearch || type === SearchType.EXTERNAL_AGENTS || requestOptions.includeSearchQueryId;
    if (searchQueryId && shouldIncludeSearchQueryId) {
        params.searchQueryId = searchQueryId;
    }

    if (prevSearchQueryId && isDataCloudSearch) {
        params.prevSearchQueryId = prevSearchQueryId;
    }

    // `type=agents` is the UI contract for Agentforce. URL-supplied or stale
    // technology filters must not override its force/copilot narrowing.
    if (type === SearchType.AGENTS) {
        filterParams = { ...filterParams, tech: [AGENTS_TECH_FILTER] };
    }

    if (type === SearchType.SLACK) {
        filterParams = { ...filterParams, tech: ensureSlackTech(filterParams.tech ?? []) };
    }

    (Object.keys(filterParams) as (keyof FilterParams)[]).forEach((param) => {
        if (param === 'achievement' && !isConsultantAchievementFilterEnabled(type)) {
            return;
        }
        const paramValue = filterParams[param];
        if (paramValue !== null && paramValue !== undefined) {
            if (Array.isArray(paramValue)) {
                params[param] = paramValue.join(';');
            } else {
                params[param] = paramValue;
            }
        }
    });
    const httpConfig: HttpRequestConfig = {
        params,
        paramsSerializer: gatewayParamsSerializer,
        headers: {
            'X-Use-New-Search': isDataCloudSearch ? 'true' : 'false',
        },
        ...(requestOptions.suppressErrorLogging ? { suppressErrorLogging: true } : {}),
    };

    const response = await http.get(getSearchEndpoint(), { ...httpConfig, signal });
    return buildSearchResults(response)[0];
};

/**
 * Keep the keywordless External Agent request contract in one place so every
 * grid participates in the same seeded ordering without depending on the
 * router-coupled search store.
 */
const getKeywordlessExternalAgentSearchResults = async ({
    page,
    pageSize,
    filterParams = {},
    seed,
    sort,
    rankingInformation = false,
    searchQueryId,
    prevSearchQueryId,
    signal,
    suppressErrorLogging,
}: KeywordlessExternalAgentSearchRequest): Promise<SearchResult> => {
    if (!isValidExternalAgentSearchSeed(seed)) {
        throw new RangeError('Keywordless External Agent searches require an unsigned 32-bit seed');
    }

    return getSearchResults(
        undefined,
        SearchType.EXTERNAL_AGENTS,
        page,
        pageSize,
        filterParams,
        sort,
        rankingInformation,
        searchQueryId,
        prevSearchQueryId,
        signal,
        { seed, suppressErrorLogging },
    );
};

const getQueryCount = async (
    keyword: string,
    type: string,
    signal?: AbortSignal,
    suppressErrorLogging?: boolean,
): Promise<number> => {
    const params: SearchApiQueryParams = {
        keyword,
        type: mapSearchTypeToApiType(type),
        page: '1',
        pageSize: '0', // this makes the response lighter, because we will not get any listings
        language: getPreferredLanguage(),
    };

    if (type === SearchType.AGENTS) {
        params.tech = AGENTS_TECH_FILTER;
        params.pageSize = '1';
    }

    if (type === SearchType.SLACK) {
        const techValues = params.tech ? params.tech.split(';') : [];
        params.tech = ensureSlackTech(techValues).join(';');
        params.pageSize = '1';
    }
    const isDataCloudSearch = FeatureFlags.FF_DATA_CLOUD_SEARCH_UI;
    const httpConfig: HttpRequestConfig = {
        params,
        paramsSerializer: gatewayParamsSerializer,
        headers: {
            'X-Use-New-Search': isDataCloudSearch ? 'true' : 'false',
        },
        ...(suppressErrorLogging ? { suppressErrorLogging: true } : {}),
    };
    const response = await http.get(getSearchEndpoint(), { ...httpConfig, signal });
    if (response.data) {
        return response.data.totalCount;
    }
    return 0;
};

const getSearchTermSuggestions = async (keyword: string): Promise<SuggestionsResult> => {
    try {
        if (getPreferredLanguage() !== ENGLISH_LANGUAGE_CODE) {
            return { suggestions: [] };
        }
        const encodedKeyword = encodeURIComponent(keyword);
        const language = getPreferredLanguage();
        const response = await http.get(
            `${getSearchTermSuggestionsEndpoint()}?keyword=${encodedKeyword}&language=${language}&limit=5`,
        );
        const suggestions = response.data.suggestions;
        const suggestionsFromTrie: Suggestion[] = suggestions.map((search_term: string) => ({
            suggestedKeyword: search_term,
            highlighted: wrapKeyword(search_term, keyword),
        }));

        return { suggestions: suggestionsFromTrie };
    } catch (error) {
        //TODO: handle errors so we don't cache empty data
        handleException(error, 'searchService.getSearchTermSuggestions');
        return { suggestions: [] };
    }
};

export { getKeywordlessExternalAgentSearchResults, getQueryCount, getSearchResults, getSearchTermSuggestions };
